<template>
  <div class="acc-safari">
    <div class="acc-safari__top">
      <div class="acc-safari__left">
        <div class="acc-safari__main-section">
          <div class="acc-safari__id">
            {{ orderNumber }}. <router-link :to="{ name: 'reservation-safari-detail', params: { id: reservation.id } }" class="acc-safari__link">
            Reservation #ATS - {{ reservation.id }}
          </router-link>
          </div>
          <div v-if="reservation.type" class="status-tag" :style="{ 'backgroundColor': reservation.type.color }">
            {{ reservation.type.name }}
          </div>
        </div>
        <div class="acc-safari__tower">
          <div class="acc-safari__name-wrapper">
            <div class="acc-safari__name">{{ reservation.short_itinerary }}</div>
            <div class="acc-safari__time">
              <div class="acc-safari__time-text">Days:</div>
              <div class="acc-safari__time-value">
                {{ reservation.days }}
              </div>
            </div>
            <div class="acc-safari__time">
              <div class="acc-safari__time-text">Nights:</div>
              <div class="acc-safari__time-value">{{ reservation.nights }}</div>
            </div>
            <div
              v-if="reservation.reservation_status.code === 'booked_provisionally' && reservation.is_deadline_expired"
              class="acc-safari__time"
            >
              <span class="acc-safari__status" style="background-color: #e85c20">
                Deadline: {{ dateFormat(reservation.reservation_deadline) }}
              </span>
            </div>
            <div
              v-if="countdown"
              :style="{ 'backgroundColor': calculateTimeDiff(countdown).difference < 1440 ? '#77b017' : calculateTimeDiff(countdown).difference < 4320 ? '#f0c427' : '#e85c20' }"
              class="acc-safari__status"
            >
                {{ calculateTimeDiff(countdown).time_string }}
              </div>
          </div>
          <div class="acc-safari__timing">
            <div class="acc-safari__time">
              <div class="acc-safari__time-text">Tour start:</div>
              <div class="acc-safari__time-value">{{ dateFormat(reservation.tour_start) }}</div>
            </div>
            <div class="acc-safari__time">
              <div class="acc-safari__time-text">Tour finish:</div>
              <div class="acc-safari__time-value">{{ dateFormat(reservation.tour_end) }}</div>
            </div>
            <div class="acc-safari__time">
              <div class="acc-safari__time-text">Pax:</div>
              <div class="acc-safari__time-value">{{ reservation.pax }}</div>
            </div>
            <div class="acc-safari__time">
              <div class="acc-safari__time-text">Cars:</div>
              <div class="acc-safari__time-value">{{ reservation.cars_required }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <button @click="is_acc_open = !is_acc_open" type="button" class="acc-safari__button" :aria-expanded="is_acc_open ? 'true' : 'false'">
      <span class="acc-safari__status-block">
        <span class="acc-safari__status" :style="{ 'backgroundColor': reservation.reservation_status.color || '#000'  }">
          {{ reservation.reservation_status.name }}
        </span>
        <img v-if="reservation.notes" src="@/assets/img/transfer-notes-icon.svg" alt="">
      </span>
      <span
          v-tooltip="{ content: reservation.lead ? reservation.lead.manager.full_name : '', placement: 'top-start', offset: 10 }"
          class="acc-safari__photo"
      >
        <img :src="getUserAvatar(reservation.lead ? reservation.lead.manager.crop.crop_url : '')" alt="Avatar">
      </span>
      <span class="acc-safari__status" :style="{ 'backgroundColor': reservation.sales_status.color || '#000'  }">
        {{ reservation.sales_status.name }}
      </span>
      <span class="acc-safari__time">
        <span class="acc-safari__time-text">Client:</span>
        <span class="acc-safari__name">
        {{ reservation.lead.name ? reservation.lead.name : `Lead #${reservation.lead?.id}` }}
      </span>
      </span>
      <span class="acc-safari__toggler" :class="{ 'acc-safari__toggler--open': is_acc_open }">
        <img src="@/assets/img/arrow-toggler-bottom-icon.svg" alt="">
      </span>
    </button>
    <SafariCancellationNotice
      v-if="reservation.sales_status.code === 'cancel' && reservation.reservation_status.code !== 'cancelled'"
      :summary="reservation.snackbar_cancellation"
      class="acc-safari__cancellation"
    />
    <transition name="slide-block">
      <div v-if="is_acc_open" class="acc-safari__dropdown">
        <div class="acc-safari__list">
          <div class="acc-safari__timing">
            <div class="acc-safari__time">
              <div class="acc-safari__time-text">Start from:</div>
              <div class="acc-safari__time-value">{{ reservation.start_from.name }}</div>
            </div>
            <div class="acc-safari__time">
              <div class="acc-safari__time-text">Finish at:</div>
              <div class="acc-safari__time-value">{{ reservation.finish_at.name }}</div>
            </div>
          </div>
          <AppSafariCard
            v-for="(item, index) in reservation.itineraries"
            :key="`safari_card_${item.date}_${index}`"
            :order-number="index + 1"
            :data="item"
            :is-clearable="false"
          />
        </div>
        <div v-if="reservation.notes" class="acc-safari__notes">
          <div class="acc-safari__note-icon">
            <img src="@/assets/img/note-green-big-icon.svg" alt="">
            <span>NOTE (by manager):</span>
          </div>
          <div class="acc-safari__text">{{ reservation.notes }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { differenceInMinutes, format, parse } from 'date-fns'
import AppSafariCard from '@/components/AppSafariCard.vue'
import SafariCancellationNotice from '@/components/SafariCancellationNotice.vue'

export default {
  name: 'AppAccordionSafari',
  components: { AppSafariCard, SafariCancellationNotice },
  props: {
    reservation: {
      type: Object,
      required: true,
    },
    orderNumber: {
      type: [Number, String],
      default: 0
    },
    countdown: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      is_acc_open: false,
    }
  },
  methods: {
    calculateTimeDiff(start) {
      const difference = differenceInMinutes(new Date(), new Date(start))
      const minutes = difference % 60
      const hours = Math.trunc((difference / 60) % 24)
      const days = Math.trunc(difference / 60 / 24)

      return {
        time_string: `${days ? `${days}d ` : ''}${hours ? `${hours}h ` : ''}${minutes}m`,
        difference
      }
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>

<style lang="sass" scoped>
.acc-safari
  margin-bottom: 10px
  border: 1px solid #dfdfdf
  border-radius: 10px
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08)
  background-color: #fff
  overflow: hidden

.acc-safari__top
  display: flex
  align-items: center
  justify-content: space-between
  padding: 7px 16px
  border-bottom: 1px solid #e8e8e8

.acc-safari__left
  display: grid
  grid-template-columns: 200px auto
  align-items: center
  gap: 20px

.acc-safari__main-section
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 10px

.acc-safari__id
  font-weight: 500

.acc-safari__photo
  width: 31px
  line-height: 0
  border-radius: 50%
  overflow: hidden

.acc-safari__link
  display: inline-block
  color: $color-text-base
  font-weight: 500
  transition: color 0.3s

  &:after
    content: ''
    display: block
    height: 2px
    border-radius: 3px
    background-color: #aae182
    transform: translateY(-1px)
    transition: opacity 0.3s

  &:hover
    &:after
      opacity: 0

.acc-safari__name-wrapper
  display: flex
  align-items: center
  gap: 10px
  margin-bottom: 6px

.acc-safari__name
  font-weight: 500

.acc-safari__nights
  color: #5f5f5f

.acc-safari__button
  position: relative
  display: grid
  grid-template-columns: 200px 31px max-content max-content max-content
  align-items: center
  gap: 20px
  width: 100%
  padding: 10px 16px
  cursor: pointer
  text-align: left

  .acc-safari__status
    transition: filter 150ms ease, box-shadow 150ms ease

  &:hover .acc-safari__status,
  &:focus-visible .acc-safari__status
    filter: brightness(0.9)
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.16)

  &:focus-visible
    outline: 2px solid $color-text-base
    outline-offset: -3px

@media (prefers-reduced-motion: reduce)
  .acc-safari__button .acc-safari__status
    transition: none

.acc-safari__status-block
  display: inline-flex
  align-items: center
  gap: 12px

.acc-safari__status
  display: inline-block
  min-width: 110px
  padding: 4px 8px 5px
  border-radius: 20px
  color: #fff
  font-size: 13px
  font-weight: 600
  text-align: center

.acc-safari__timing
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 5px 20px
  margin-bottom: 6px

  &:last-child
    margin-bottom: 0

.acc-safari__time
  display: flex
  gap: 6px

.acc-safari__time-text
  color: #5f5f5f

.acc-safari__time-value
  color: #181818
  font-weight: 500

.acc-safari__toggler
  position: absolute
  top: 14px
  right: 18px
  transition: transform 0.3s

.acc-safari__toggler--open
  transform: rotate(180deg)

.acc-safari__cancellation
  margin: 0 16px 14px

.acc-safari__dropdown
  border-top: 1px solid #e8e8e8
  background-color: #fafafa

.acc-safari__list
  padding: 14px

.acc-safari__room
  margin-bottom: 10px

  &:last-child
    margin-bottom: 0

.acc-safari__notes
  display: flex
  align-items: center
  gap: 8px
  padding: 12px 18px 10px
  border-top: 1px solid #e8e8e8

.acc-safari__note-icon
  flex-shrink: 0
  display: flex
  align-items: center
  gap: 10px
  color: #181818
  font-weight: 500

.acc-safari__text
  color: #5f5f5f

.acc-safari__right
  display: flex
  align-items: center
  gap: 10px

// ------------------------ slide-block
.slide-block-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-block-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-block-enter-to, .slide-block-leave
  max-height: 500px
  overflow: hidden

.slide-block-enter, .slide-block-leave-to
  overflow: hidden
  max-height: 0
</style>
